import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import helpers from 'utils/helpers';
import TemplateFormModal from './TemplateFormModal';

const TemplateManager = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadTemplates = async () => {
    try {
      const res = await apiGet(apiPath.aiInterviewTemplateList, {});
      setTemplates(res?.data?.results || []);
    } catch (error) {
      console.log('error loading templates:', error);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      const res = editingTemplate
        ? await apiPut(`${apiPath.aiInterviewTemplate}/${editingTemplate._id}`, formData)
        : await apiPost(apiPath.aiInterviewTemplate, formData);

      if (res?.data?.success) {
        notification.success(res?.data?.message);
        setShowModal(false);
        loadTemplates();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (error) {
      console.log('error saving template:', error);
      notification.error('Failed to save template.');
    }
  };

  const handleDelete = (template) => {
    helpers.alertFunction(
      `Are you sure you want to delete the '${template.role}' template?`,
      template,
      async () => {
        try {
          const res = await apiDelete(`${apiPath.aiInterviewTemplate}/${template._id}`);
          if (res?.data?.success) {
            notification.success(res?.data?.message);
            loadTemplates();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          console.log('error deleting template:', error);
          notification.error('Failed to delete template.');
        }
      },
      false
    );
  };

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('INTERVIEW_TEMPLATES') || 'Interview Templates'}</h3>
        <button
          onClick={handleCreate}
          className="bg-LightBlue text-white font-normal text-sm px-6 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
        >
          + New Template
        </button>
      </div>

      <div className="overflow-x-auto relative rounded-lg border">
        <table className="w-full text-xs text-left text-[#A5A5A5]">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE]">
            <tr>
              <th scope="col" className="py-3 px-6">Role</th>
              <th scope="col" className="py-3 px-6">Skills</th>
              <th scope="col" className="py-3 px-6">Difficulty</th>
              <th scope="col" className="py-3 px-6">Duration (min)</th>
              <th scope="col" className="py-3 px-6"># Questions</th>
              <th scope="col" className="py-3 px-6 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {templates?.map((template) => (
              <tr key={template._id} className="bg-white border-b">
                <td className="py-4 px-6 border-r text-gray-900">{template.role}</td>
                <td className="py-4 px-6 border-r text-gray-900">{template.skills?.join(', ')}</td>
                <td className="py-4 px-6 border-r text-gray-900 capitalize">{template.difficulty}</td>
                <td className="py-4 px-6 border-r text-gray-900">{template.duration}</td>
                <td className="py-4 px-6 border-r text-gray-900">{template.totalQuestions}</td>
                <td className="py-4 px-6 border-l text-gray-900">
                  <ul className="items-center justify-center flex gap-2">
                    <li>
                      <button
                        onClick={() => handleEdit(template)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleDelete(template)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </li>
                  </ul>
                </td>
              </tr>
            ))}
            {isEmpty(templates) && (
              <tr className="bg-white text-center border-b">
                <td className="py-4 px-6" colSpan={6}>
                  {t('O_NO_RECORD_FOUND') || 'No templates yet — create one to get started.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <TemplateFormModal
          setShowModal={setShowModal}
          onSave={handleSave}
          initialData={editingTemplate}
        />
      )}
    </div>
  );
};

export default TemplateManager;